/**
 * react-i18next initialization for the authenticated web app.
 *
 * `en` is the source of truth; every other locale must provide the same keys
 * (enforced by the catalog-parity test). The active language is initialized from
 * the browser on first load, then synced to the user's saved `ui_language`
 * preference once settings load (see LanguageProvider).
 *
 * A module-level `getActiveLanguage()` mirror is kept in sync so the plain
 * `apiFetch` util can send an `Accept-Language` header without importing React.
 */

import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'

import { DEFAULT_LOCALE, SUPPORTED_LOCALE_CODES } from './locales'
import en from './locales/en/common.json'
import es from './locales/es/common.json'
import it from './locales/it/common.json'
import nl from './locales/nl/common.json'
import pt from './locales/pt/common.json'

export const resources = {
  en: { common: en },
  es: { common: es },
  it: { common: it },
  nl: { common: nl },
  pt: { common: pt },
} as const

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALE_CODES,
    // Map regional variants (es-ES, es-MX) down to the base locale we ship.
    load: 'languageOnly',
    defaultNS: 'common',
    ns: ['common'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // Prefer an explicit prior choice, then the browser. The user's saved
      // server-side preference is applied imperatively in LanguageProvider.
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'savorias_ui_language',
      caches: ['localStorage'],
    },
  })

// --- Active-language mirror for non-React consumers (e.g. apiFetch) ---
let activeLanguage: string = i18n.resolvedLanguage || DEFAULT_LOCALE

i18n.on('languageChanged', (lng) => {
  activeLanguage = lng
})

/** Current UI language, for use outside React (sent as Accept-Language). */
export function getActiveLanguage(): string {
  return activeLanguage
}

export default i18n
